import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DataGrid.css'; // Custom styles (optional)

import CustomLoadingOverlay from './CustomLoadingOverlay/LoadingOverlay'
import CustomNoRowsOverlay from './CustomNoRowsOverlay/NoRowsOverlay'
import CustomPagination from './CustomPagination/CustomPagination';

const DataGrid = ({
  gridRef,
  isLoading, // Boolean
  error, // Error
  columnDefs,
  rowData,
  gridOptions = {},
  className = '',
  ...rest
}) => {
  const [gridApi, setGridApi] = useState(null)
  const [suppressNoRowsOverlay, setSuppressNoRowsOverlay] = useState(true)
  const [paginationTotalRecords, setPaginationTotalRecords] = useState(0)
  const [paginationTotalPages, setPaginationTotalPages] = useState(0)
  const [paginationPageSize, setPaginationPageSize] = useState(10)
  const [paginationCurrentPage, setPaginationCurrentPage] = useState(1)
  const thisGridRef = useRef()
  const ref = gridRef ?? thisGridRef

  useEffect(() => {
    if (rowData) {
      setPaginationTotalRecords(rowData.length)
      setPaginationTotalPages(Math.ceil(rowData.length / paginationPageSize))
    }
  }, [rowData, paginationPageSize])

  useEffect(() => {
    if (!gridApi) return

    gridApi.paginationSetPageSize(paginationPageSize)
    // Returns the 0-based index of the page
    const page = gridApi.paginationGetCurrentPage()
    setPaginationCurrentPage(page + 1)
  }, [gridApi, paginationPageSize])

  useEffect(() => {
    if (!gridApi) return

    // Sets the 0-based index of the page
    gridApi.paginationGoToPage(paginationCurrentPage - 1)
  }, [gridApi, paginationCurrentPage])

  useEffect(() => {
    if (!gridApi) return
    setPaginationTotalRecords(gridApi.getDisplayedRowCount())
    setPaginationTotalPages(gridApi.paginationGetTotalPages())
    setPaginationPageSize(gridApi.paginationGetPageSize())
    setPaginationCurrentPage(gridApi.paginationGetCurrentPage() + 1)
  }, [gridApi, gridOptions.quickFilterText])

  useEffect(() => {
    let timeoutFunc
    if (isLoading) {
      timeoutFunc = setTimeout(() => showLoadingOverlay(gridApi))
    } else if (paginationTotalRecords === 0) {
      timeoutFunc = setTimeout(() => showNoDataOverlay(gridApi))
    } else {
      hideOverlay(gridApi)
    }
    return () => clearTimeout(timeoutFunc)
  }, [isLoading, gridApi, paginationTotalRecords])

  const onGridReady = ({ api }) => {
    if (ref) ref.current.api = api
    setGridApi(api)
  }

  const showLoadingOverlay = (gridApi) => {
    setSuppressNoRowsOverlay(true)
    gridApi?.showLoadingOverlay()
  }

  const showNoDataOverlay = (gridApi) => {
    setSuppressNoRowsOverlay(false)
    gridApi?.showNoRowsOverlay()
  }

  const hideOverlay = (gridApi) => {
    gridApi?.hideOverlay()
  }

  const defaultColumnDef = useMemo(() => {
    return {
      flex: 1,
      sortable: true,
      resizable: false,
      filter: false,
      unSortIcon: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      // autoHeight: true,
      // minWidth: 200,
      // type: 'rightAligned',
      // cellStyle: { textAlign: 'left' },
      ...gridOptions.defaultColDef,
    }
  }, [gridOptions])

  return (
    <div
      className={`data-grid-wrapper ag-theme-alpine ag-theme-custom ${className}`}
      style={{ width: '100%', height: '100%' }}
    >
      <AgGridReact
        ref={ref}
        columnDefs={columnDefs}
        rowData={rowData}
        multiSortKey="ctrl"
        pagination={true}
        paginationPageSize={paginationPageSize}
        suppressPaginationPanel={true}
        suppressNoRowsOverlay={suppressNoRowsOverlay}
        defaultColDef={defaultColumnDef}
        suppressCellFocus={true}
        domLayout="autoHeight"
        onGridReady={onGridReady}
        noRowsOverlayComponent={CustomNoRowsOverlay}
        loadingOverlayComponent={CustomLoadingOverlay}
        icons={{
          sortDescending: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.86058 11.8548C4.6733 11.489 4.22497 11.3443 3.85919 11.5316C3.49343 11.7189 3.34874 12.1673 3.53604 12.533C4.86118 15.1209 6.22505 16.6311 8.60495 17.9695C9.47085 18.4565 10.5292 18.4565 11.395 17.9695C13.775 16.6311 15.1388 15.1209 16.464 12.533C16.6513 12.1673 16.5065 11.7189 16.1408 11.5316C15.7751 11.3443 15.3268 11.489 15.1394 11.8548C13.9293 14.2183 12.7577 15.4958 10.6656 16.6725C10.2527 16.9048 9.74736 16.9048 9.33441 16.6725C7.24226 15.4958 6.07079 14.2183 4.86058 11.8548Z" fill="#100F2E"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.86058 8.14522C4.6733 8.51099 4.22497 8.65568 3.85919 8.46839C3.49343 8.28111 3.34874 7.83276 3.53604 7.467C4.86118 4.87903 6.22505 3.36895 8.60495 2.03051C9.47085 1.54354 10.5292 1.54354 11.395 2.03051C13.775 3.36895 15.1388 4.87903 16.464 7.467C16.6513 7.83276 16.5065 8.28111 16.1408 8.46839C15.7751 8.65568 15.3268 8.51099 15.1394 8.14522C13.9293 5.78174 12.7577 4.50415 10.6656 3.32756C10.2527 3.09532 9.74736 3.09532 9.33441 3.32756C7.24226 4.50415 6.07079 5.78174 4.86058 8.14522Z" fill="#C8C8D0"/>
          </svg>`,
          sortAscending: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.86058 11.8548C4.6733 11.489 4.22497 11.3443 3.85919 11.5316C3.49343 11.7189 3.34874 12.1673 3.53604 12.533C4.86118 15.1209 6.22505 16.6311 8.60495 17.9695C9.47085 18.4565 10.5292 18.4565 11.395 17.9695C13.775 16.6311 15.1388 15.1209 16.464 12.533C16.6513 12.1673 16.5065 11.7189 16.1408 11.5316C15.7751 11.3443 15.3268 11.489 15.1394 11.8548C13.9293 14.2183 12.7577 15.4958 10.6656 16.6725C10.2527 16.9048 9.74736 16.9048 9.33441 16.6725C7.24226 15.4958 6.07079 14.2183 4.86058 11.8548Z" fill="#C8C8D0"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.86058 8.14522C4.6733 8.51099 4.22497 8.65568 3.85919 8.46839C3.49343 8.28111 3.34874 7.83276 3.53604 7.467C4.86118 4.87903 6.22505 3.36895 8.60495 2.03051C9.47085 1.54354 10.5292 1.54354 11.395 2.03051C13.775 3.36895 15.1388 4.87903 16.464 7.467C16.6513 7.83276 16.5065 8.28111 16.1408 8.46839C15.7751 8.65568 15.3268 8.51099 15.1394 8.14522C13.9293 5.78174 12.7577 4.50415 10.6656 3.32756C10.2527 3.09532 9.74736 3.09532 9.33441 3.32756C7.24226 4.50415 6.07079 5.78174 4.86058 8.14522Z" fill="#100F2E"/>
          </svg>`,
          sortUnSort: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.86058 8.14522C4.6733 8.51099 4.22497 8.65568 3.85919 8.46839C3.49343 8.28111 3.34874 7.83276 3.53604 7.467C4.86118 4.87903 6.22505 3.36895 8.60495 2.03051C9.47085 1.54354 10.5292 1.54354 11.395 2.03051C13.775 3.36895 15.1388 4.87903 16.464 7.467C16.6513 7.83276 16.5065 8.28111 16.1408 8.46839C15.7751 8.65568 15.3268 8.51099 15.1394 8.14522C13.9293 5.78174 12.7577 4.50415 10.6656 3.32756C10.2527 3.09532 9.74736 3.09532 9.33441 3.32756C7.24226 4.50415 6.07079 5.78174 4.86058 8.14522ZM4.86058 11.8549C4.6733 11.4891 4.22497 11.3444 3.85919 11.5317C3.49343 11.719 3.34874 12.1673 3.53604 12.5331C4.86118 15.121 6.22505 16.6311 8.60495 17.9696C9.47085 18.4566 10.5292 18.4566 11.395 17.9696C13.775 16.6311 15.1388 15.121 16.464 12.5331C16.6513 12.1673 16.5065 11.719 16.1408 11.5317C15.7751 11.3444 15.3268 11.4891 15.1394 11.8549C13.9293 14.2184 12.7577 15.4959 10.6656 16.6726C10.2527 16.9048 9.74736 16.9048 9.33441 16.6726C7.24226 15.4959 6.07079 14.2184 4.86058 11.8549Z" fill="#C8C8D0"/>
          </svg>`
        }}
        {...gridOptions}
        {...rest}
      />
      <CustomPagination
        gridRef={ref}
        totalRecords={paginationTotalRecords}
        totalPages={paginationTotalPages}
        paginationCurrentPage={paginationCurrentPage}
        paginationPageSize={paginationPageSize}
        setPaginationPageSize={setPaginationPageSize}
        setPaginationCurrentPage={setPaginationCurrentPage}
      />
    </div>
  );
};

export default DataGrid;